<template>
  <div 
    ref="baseSelectRef" 
    class="baseSelect" 
    :class="{
      hasError,
      hasSuccess,
      [`${el}--open`]: open,
      [`${el}--hasNotification`]: (!!hasError || hasSuccess),
      isDisabled: ($attrs.disabled!==undefined && $attrs.disabled!==false)
    }"
  >
    <div
      :class="`${el}__input`"
      @click="onClick"
    >
      <PflegeInput
        :label="label"
        :model-value="selectedLabel"
        :has-error="hasError"
        :clearable="false"
        :has-success="hasSuccess"
        :error-message="errorMessage"
        :persistent-hint="true"
        autocomplete="off"
        readonly
        v-bind="$attrs"
      >
        <template #hint>
          <slot name="hint"></slot>
        </template>
      </PflegeInput>
      <Icon size="l" :class="`${el}__arrowIcon`" :color="($attrs.disabled!==undefined && $attrs.disabled!==false) ? 'neutral-30' : 'blue'" name="chevron-down-line" />
    </div>
    <div :class="`${el}__dropdownMenu pflegescrollbar`">
      <div
        v-for="option in options"
        :key="option.label"
        :class="[
          `${el}__dropdownOption`,
          {
            [`${el}__dropdownOption--disabled`]: option.disabled,
          },
        ]"
        @click="updateOption(option)"
      >
        {{ option.label }}
        <Icon
          v-if="option.label && option.label === selectedLabel"
          size="l"
          :class="`${el}__checkIcon`"
          color="blue"
          name="check-line"
        />
      </div>
    </div>
  </div>
</template>


<script setup>
import { onMounted, ref, inject, watch, useAttrs, onBeforeUnmount, useTemplateRef } from 'vue'
import PflegeInput from '../Input'
import Icon from '../Icon'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: [String, Number],
    required: true,
  },
  label: {
    type: String,
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: null,
  },
  trackingLabel: {
    type: String,
    default: null,
  },
  hasSuccess: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])
const Track = inject('track', props.trackingLabel)
const attrs = useAttrs()

const el = 'baseSelect'

const selectedLabel = ref('')
const open = ref(false)
const selectedOption = ref({})

const baseSelectRef = useTemplateRef('baseSelectRef')
const outsideClickSelectListener = (e) => {
  if (!e.composedPath().includes(baseSelectRef.value)) {
    closeDropdown()
  }
}
onMounted(() => {
  if (props.modelValue) {
    updateOption(props.options.find((o) => o.value === props.modelValue))
  }
  window.addEventListener('click', outsideClickSelectListener)
})
onBeforeUnmount(() => {
  window.removeEventListener('click', outsideClickSelectListener)
})

function onClick() {
  if (attrs.disabled === undefined || attrs.disabled === false) {
    if (open.value) {
      closeDropdown()
    } else {
      openDropdown()
    }
  }
}

function openDropdown() {
  open.value = true
}

function closeDropdown() {
  open.value = false
}

function updateOption(option) {
  if (option.disabled) {
    return
  }

  selectedOption.value = option
  selectedLabel.value = option.label
  emit('update:modelValue', selectedOption.value.value)
  track('select')
  closeDropdown()
}

function track(action) {
  if (props.trackingLabel) {
    Track(`form-dropdown-${action}`, props.trackingLabel)
  }
}

watch(open, () => {
  track(open.value ? 'focus' : 'focus-out')
})
</script>



<style scoped lang="scss">
@use '../../../styles' as index;

.baseSelect {
  $block: &;
  $m-open: #{$block}--open;
  $m-hasNotification: #{$block}--hasNotification;
  position: relative;
  cursor: pointer;

  &.isDisabled {
    pointer-events: none;
  }

  &__input {
    .base-input.isDisabled ~ .icon{
      --color: var(--neutral-20) !important;
      pointer-events: none;
    }
  }

  input{
    cursor: pointer;
  }

  &__arrowIcon {
    position: absolute;
    top: 2.625rem;
    right: 1rem;
    transform: translateY(-50%) rotate(0deg);
    transition: transform var(--time2) ease-in-out;

    #{$m-hasNotification} & {
      right: 2.5rem;
    }

    #{$m-open} & {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  &__dropdownMenu {
    display: none;
    background-color: var(--white);
    border: 1px solid var(--mint-darker);
    border-radius: 0 0 .25rem .25rem;
    overflow: hidden auto;
    cursor: pointer;
    position: absolute;
    top: 4rem;
    left: 0;
    z-index: 10;
    width: 100%;
    max-height: 15rem;

    #{$m-open} & {
      display: block;
      border: 1px solid var(--mint-darker);
    }
  }

  &__dropdownOption {
    display: flex;
    justify-content: space-between;
    align-items: center;
    white-space: normal;
    padding: .85rem .75rem;
    color: var(--font-color-primary);
    min-height: 1rem;
    transition: color index.$animation__transition--fast, background-color index.$animation__transition--fast;

    &--disabled {
      color: var(--neutral-40);
      background-color: var(--neutral-20);
      cursor: default;
    }

    &:not(#{$block}__dropdownOption--disabled) {
      &:hover {
        background-color: var(--mint-darker);
        color: var(--white);
      }
    }
  }
}
</style>
